import {
  type HTMLAttributes,
  type JSX,
  type FocusEvent as ReactFocusEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { installHoverTips } from './hover-tip';
import { cx } from './primitives';
import {
  drawerToggleAria,
  readSidebarCollapsed,
  SIDEBAR_STORAGE_KEY,
  sidebarToggleAria,
  writeSidebarCollapsed,
} from './sidebar-state';

/**
 * The shared application shell: a top bar, a collapsible sidebar and a content
 * column. Two portals ran two hand-written copies of this before it existed, and
 * neither of them worked on a phone.
 *
 * Every string is passed in already translated - no copy is hardcoded in a
 * component (PRD NFR-006) - which is why the labels for the hamburger and the
 * collapse control are required props rather than defaults.
 *
 * Responsive behaviour is CSS, not JavaScript (see layout.css): there is no
 * resize listener and no media-query hook, so the first paint is already correct
 * at whatever width the document opened at.
 */

// ---------------------------------------------------------------------------
// AppShell
// ---------------------------------------------------------------------------

export interface AppShellLabels {
  /** e.g. "Skip to content". */
  readonly skipToContent: string;
  /** Accessible name of the hamburger, e.g. "Open menu". */
  readonly openMenu: string;
  /** Accessible name of the hamburger when the drawer is open, e.g. "Close menu". */
  readonly closeMenu: string;
  /** Accessible name of the desktop collapse control, e.g. "Collapse sidebar". */
  readonly collapseSidebar: string;
  /** Accessible name of the same control when collapsed, e.g. "Expand sidebar". */
  readonly expandSidebar: string;
}

export interface AppShellProps {
  /** Rendered inside the top bar, after the brand. */
  brand?: ReactNode;
  /** Rendered at the right edge of the top bar - identity, locale, sign out. */
  topBarActions?: ReactNode;
  /** The sidebar's navigation. Usually a list of `SidebarLink`. */
  nav: ReactNode;
  /** Rendered above the navigation, inside the sidebar. */
  sidebarHeader?: ReactNode;
  /** Rendered at the bottom of the sidebar. Hidden in rail mode. */
  sidebarFooter?: ReactNode;
  /** Accessible name for the `<nav>` landmark. Already translated. */
  navLabel: string;
  labels: AppShellLabels;
  /**
   * The content measure. `wide` caps a dashboard at a readable line length,
   * `narrow` is the guest funnel's reading column, `full` opts out.
   */
  width?: 'wide' | 'narrow' | 'full';
  /** Override the localStorage key. One shell per portal needs no override. */
  storageKey?: string;
  /** The id the skip link targets. Kept overridable so an app can keep its own. */
  mainId?: string;
  children: ReactNode;
}

export function AppShell({
  brand,
  topBarActions,
  nav,
  sidebarHeader,
  sidebarFooter,
  navLabel,
  labels,
  width = 'wide',
  storageKey = SIDEBAR_STORAGE_KEY,
  mainId = 'cib-main',
  children,
}: AppShellProps): JSX.Element {
  const sidebarId = useId();
  /*
   * Expanded on the first paint, always. Reading localStorage during render would
   * make the markup depend on a browser API this component is also rendered
   * without, and a shell that boots collapsed for one frame is a visible jump.
   * The effect below applies the stored preference immediately after mount.
   */
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  /*
   * The collapsed rail opens over the content while the pointer rests on it,
   * or while the keyboard is inside it, and closes again when either leaves.
   * It is a PEEK, never a change of preference: nothing is written to storage
   * and the content column keeps the rail's width, so the page underneath does
   * not reflow for a mouse passing through.
   */
  const [peek, setPeek] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const stored = typeof window === 'undefined' ? null : window.localStorage;
    setCollapsed(readSidebarCollapsed(stored, storageKey));
  }, [storageKey]);

  const toggleCollapsed = useCallback(() => {
    /*
     * Closed whichever way the click goes. Collapsing from under a pointer that
     * is still on the panel must actually collapse it - left open, the peek
     * would hold the panel wide and the click would look like it did nothing -
     * and it only reopens once the pointer has left and come back.
     */
    setPeek(false);
    setCollapsed((previous) => {
      const next = !previous;
      writeSidebarCollapsed(
        typeof window === 'undefined' ? null : window.localStorage,
        next,
        storageKey,
      );
      return next;
    });
  }, [storageKey]);

  /*
   * Hover means a real pointer. A tap on a touch screen wide enough for the
   * rail fires compatibility mouse events too, and a peek opened by a tap
   * would stay open until the next tap somewhere else.
   */
  const openPeekOnHover = useCallback(() => {
    if (typeof window.matchMedia === 'function' && !window.matchMedia('(hover: hover)').matches) {
      return;
    }
    setPeek(true);
  }, []);

  const closePeekOnLeave = useCallback((event: ReactMouseEvent<HTMLElement>) => {
    // A keyboard user tabbing through the rail keeps it open under a mouse
    // that happens to drift off it.
    const focused = document.activeElement;
    if (focused !== null && event.currentTarget.contains(focused) && isFocusVisible(focused)) {
      return;
    }
    setPeek(false);
  }, []);

  const openPeekOnFocus = useCallback((event: ReactFocusEvent<HTMLElement>) => {
    // Keyboard focus only: a mouse click focuses a link too, and a rail that
    // stayed open because its link still held focus would never close.
    if (isFocusVisible(event.target)) setPeek(true);
  }, []);

  const closePeekOnBlur = useCallback((event: ReactFocusEvent<HTMLElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && event.currentTarget.contains(next)) return;
    setPeek(false);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    // Focus goes back to the control that opened the drawer. Without this a
    // keyboard user lands at the top of the document after every close.
    menuButtonRef.current?.focus();
  }, []);

  /*
   * Escape closes the drawer. The drawer is not a `<dialog>` - it is a permanent
   * column at desktop widths and only an overlay below lg - so the platform does
   * not hand us Escape the way it does for `Modal`.
   */
  // Every `title` in the portal shows at once, not a second late (hover-tip.ts).
  useEffect(() => installHoverTips(), []);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') closeDrawer();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [drawerOpen, closeDrawer]);

  return (
    <div
      className="cib-root cib-shell"
      data-sidebar-collapsed={collapsed ? 'true' : 'false'}
      data-sidebar-peek={collapsed && peek ? 'open' : 'closed'}
      data-drawer={drawerOpen ? 'open' : 'closed'}
    >
      <a className="cib-shell__skip" href={`#${mainId}`}>
        {labels.skipToContent}
      </a>

      <header className="cib-topbar">
        <button
          ref={menuButtonRef}
          type="button"
          className="cib-topbar__menu"
          onClick={() => (drawerOpen ? closeDrawer() : setDrawerOpen(true))}
          aria-label={drawerOpen ? labels.closeMenu : labels.openMenu}
          {...drawerToggleAria(drawerOpen, sidebarId)}
        >
          <HamburgerGlyph />
        </button>
        {brand}
        <span className="cib-topbar__spacer" />
        {topBarActions !== undefined ? (
          <div className="cib-topbar__actions">{topBarActions}</div>
        ) : null}
      </header>

      <div className="cib-shell__body">
        {/* A button and not a div: the scrim is a real way to close the drawer and
            has to be reachable, though a keyboard user reaches Escape first. */}
        <button
          type="button"
          className="cib-shell__scrim"
          onClick={closeDrawer}
          aria-label={labels.closeMenu}
          tabIndex={drawerOpen ? 0 : -1}
        />

        <aside
          className={cx('cib-sidebar', sidebarHeader === undefined && 'cib-sidebar--headless')}
          id={sidebarId}
          onMouseEnter={collapsed ? openPeekOnHover : undefined}
          onMouseLeave={closePeekOnLeave}
          onFocus={collapsed ? openPeekOnFocus : undefined}
          onBlur={closePeekOnBlur}
        >
          {/*
            The collapse control sits at the TOP of the panel, where every
            desktop application puts it, and it is the panel glyph rather than a
            worded button: the control is used constantly and read once. With a
            header it ends the header's row; without one (`--headless`) it ends
            the FIRST LINK's row, so no row of the panel is spent on it alone.
            Below lg the whole row is `display: contents` and the button is
            hidden - collapsing means nothing when the sidebar is already a
            drawer.
          */}
          <div className="cib-sidebar__top">
            {sidebarHeader !== undefined ? (
              <div className="cib-sidebar__header">{sidebarHeader}</div>
            ) : null}

            <button
              type="button"
              className="cib-sidebar__toggle"
              onClick={toggleCollapsed}
              aria-label={collapsed ? labels.expandSidebar : labels.collapseSidebar}
              {...sidebarToggleAria(collapsed, sidebarId)}
            >
              <SidebarToggleGlyph collapsed={collapsed} />
            </button>
          </div>

          <nav className="cib-sidebar__nav" aria-label={navLabel}>
            {nav}
          </nav>

          {sidebarFooter !== undefined ? (
            <div className="cib-sidebar__footer">{sidebarFooter}</div>
          ) : null}
        </aside>

        <main
          className={cx('cib-shell__main', width !== 'full' && `cib-shell__main--${width}`)}
          id={mainId}
          tabIndex={-1}
        >
          {children}
        </main>
      </div>
    </div>
  );
}

/**
 * `:focus-visible` is the browser's own answer to "did the keyboard put focus
 * here", which is exactly the question. An engine that cannot parse the
 * selector answers no, and the rail simply does not peek on focus there.
 */
function isFocusVisible(element: Element): boolean {
  try {
    return element.matches(':focus-visible');
  } catch {
    return false;
  }
}

/**
 * The panel glyph, drawn rather than imported: a frame with the sidebar column
 * marked off inside it and a chevron pointing the way the click will move the
 * panel. It is the convention every editor and admin console uses for this
 * control, so it needs no label to be understood - and the button carries the
 * worded `aria-label` for everyone who does not see it.
 */
function SidebarToggleGlyph({ collapsed }: { collapsed: boolean }): JSX.Element {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="2.75" y="3.75" width="14.5" height="12.5" rx="2.25" />
      <path d="M8 3.75v12.5" />
      {/* Points left while expanded (the click narrows the panel), right once
          collapsed. The direction IS the affordance. */}
      <path d={collapsed ? 'm11.75 8 2 2-2 2' : 'm14 8-2 2 2 2'} />
    </svg>
  );
}

/** Three bars, drawn rather than imported: this package carries no icon set. */
function HamburgerGlyph(): JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <path
        d="M3 5h14M3 10h14M3 15h14"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// SidebarLink
// ---------------------------------------------------------------------------

export interface SidebarLinkContentProps {
  /** Already translated. Stays in the accessibility tree in rail mode. */
  label: string;
  /**
   * The rail glyph. Falls back to the label's first character, so a portal
   * without an icon set still collapses to something legible.
   */
  icon?: ReactNode;
}

/**
 * The inside of a sidebar row: a glyph column and a label.
 *
 * There is no `SidebarLink` wrapper, deliberately. Each portal has its own
 * router - wouter's `<Link>` here, a plain anchor in a static page - so the app
 * renders its own element with `className="cib-sidebar__link"` and its own
 * `aria-current`, and puts this inside it. Injecting a router into this package
 * would buy nothing and cost it a dependency.
 *
 * The label is always in the DOM: rail mode hides it visually (see layout.css)
 * but leaves it as the link's accessible name, so a collapsed rail is a column of
 * named links rather than of unlabelled squares.
 */
export function SidebarLinkContent({ label, icon }: SidebarLinkContentProps): JSX.Element {
  return (
    <>
      <span className="cib-sidebar__glyph" aria-hidden="true">
        {icon ?? label.slice(0, 1)}
      </span>
      <span className="cib-sidebar__label">{label}</span>
    </>
  );
}

// ---------------------------------------------------------------------------
// Section - a heading block that can stay put over a long list
// ---------------------------------------------------------------------------

export interface SectionProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /**
   * Pins the heading under the top bar while the list scrolls. Worth it on a long
   * list, distracting on a short one, so it is opt-in.
   */
  sticky?: boolean;
  /** Renders the title as an `<h1>`. One per screen. */
  as?: 'h1' | 'h2';
  children?: ReactNode;
}

export function Section({
  title,
  subtitle,
  actions,
  sticky = false,
  as = 'h2',
  className,
  children,
  ...rest
}: SectionProps): JSX.Element {
  const Heading = as;
  return (
    <section {...rest} className={cx('cib-section', className)}>
      <div className={cx('cib-section__header', sticky && 'cib-section__header--sticky')}>
        <div>
          <Heading className="cib-section__title">{title}</Heading>
          {subtitle !== undefined ? <p className="cib-section__subtitle">{subtitle}</p> : null}
        </div>
        {actions !== undefined ? <div className="cib-section__actions">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}
